/*
 * The lifecycle of a video job that already exists: poll it, resume it, confirm
 * it, cancel it. Creating one is `generation/services/videoGenerationApi` —
 * keeping both here made content depend on generation and generation depend on
 * content at the same time.
 */
import type { ChapterPlace, GenerationMetadata, JobError, JobProgress, ProcessingStatus } from '@/shared/types';
import { ApiError, deleteJson, getJson, postJson, resolveApiUrl } from '@/shared/api/apiClient';
import {
  LIBRARY_BACKEND,
  cancelPath,
  isTerminalStatus,
  mapChapterPlace,
  mapJobProgress,
  normalizeMode,
  normalizeStatus,
  normalizeStyle,
  resumePath,
  statusPath,
  type ChapterPlaceApi,
  type TaskApiItem,
} from '@/shared/api/backendContract';

type GenerateApiResponse = {
  job_id: string;
  status: ProcessingStatus;
  waiting_for?: ChapterPlaceApi | null;
};

type DeleteApiResponse = {
  video_id?: string;
  job_id?: string;
  deletion_status?: string;
};

type StatusApiResponse = {
  job_id: string;
  status: ProcessingStatus;
  detail?: string;
  video_url?: string;
  session_id?: string;
  user_id?: string;
  model_name?: string;
  duration?: number;
  max_scenes?: number | null;
  aspect_ratio?: string;
  provider?: string;
  raw_text?: string;
  prompt_list?: string[];
  last_prompt?: string;
  last_scene_idx?: number;
  // Present while a job sits in awaiting_confirmation -- the cost/ETA pair the
  // confirmation modal renders. Declared here rather than cast at the read site
  // so the status payload stays fully typed.
  estimated_cost?: number | null;
  estimated_duration_seconds?: number | null;
};

export type JobStatusResult = {
  jobId: string;
  status: ProcessingStatus;
  detail?: string;
  videoUrl?: string;
  sourceText?: string;
  // Present once the backend returns it (e.g. its auto-generated title); used to
  // sync the in-session job title to the backend's canonical one.
  title?: string;
  metadata?: GenerationMetadata;
  thumbnailUrl?: string;
  // Presigned URL to the backend-generated WebVTT caption track, when present.
  vttUrl?: string;
  // Structured failure detail, present only on a failed job.
  error?: JobError | null;
  progress?: JobProgress;
  estimatedCost?: number;
  estimatedDurationSeconds?: number;
  // The finished video's real runtime. Detail-route only in practice: it is on
  // the list shape too, but the library maps that separately.
  durationSeconds?: number;
  waitingFor?: ChapterPlace;
};

export async function fetchVideoStatus(jobId: string): Promise<JobStatusResult> {
  if (LIBRARY_BACKEND === 'videos') {
    // GET /api/v1/videos/{id} is the full shape — unlike the list, which drops
    // `progress` and `prompt_list` and truncates `text_input`. This is the only
    // route that returns the complete source text.
    const task = await getJson<TaskApiItem>(statusPath(jobId));
    return {
      jobId: task.task_id,
      status: normalizeStatus(task.status),
      videoUrl: resolveApiUrl(task.video_url ?? task.download_url),
      sourceText: task.text_input,
      durationSeconds: task.runtime_seconds ?? undefined,
      title: task.title,
      thumbnailUrl: task.thumbnail_url ? resolveApiUrl(task.thumbnail_url) : undefined,
      vttUrl: task.vtt_url ? resolveApiUrl(task.vtt_url) : undefined,
      error: task.error ?? null,
      progress: mapJobProgress(task.progress ?? null),
      estimatedCost: task.estimated_cost ?? undefined,
      estimatedDurationSeconds: task.estimated_duration_seconds ?? undefined,
      waitingFor: mapChapterPlace(task.waiting_for),
      metadata: {
        aspectRatio: task.aspect_ratio,
        duration: task.duration,
        provider: task.provider,
        mode: normalizeMode(task.mode),
        style: normalizeStyle(task.style),
        promptList: task.prompt_list,
      },
    };
  }

  const response = await getJson<StatusApiResponse>(statusPath(jobId));
  return {
    jobId: response.job_id,
    // Normalize so a backend spelling like 'in_progress' maps to a polled state
    // (matching the library mapper) instead of being dropped by the poller.
    status: normalizeStatus(response.status),
    detail: response.detail,
    videoUrl: resolveApiUrl(response.video_url),
    sourceText: response.raw_text,
    estimatedCost: response.estimated_cost ?? undefined,
    estimatedDurationSeconds: response.estimated_duration_seconds ?? undefined,
    metadata: {
      sessionId: response.session_id,
      userId: response.user_id,
      modelName: response.model_name,
      duration: response.duration,
      maxScenes: response.max_scenes,
      aspectRatio: response.aspect_ratio,
      provider: response.provider,
      promptList: response.prompt_list,
      lastPrompt: response.last_prompt,
      lastSceneIdx: response.last_scene_idx,
    },
  };
}

// Asks the backend to resume a failed/incomplete job. Returns the (normalized)
// status the job restarts in so the caller can flip it back to a polled state.
export async function resumeVideo(jobId: string): Promise<{ jobId: string; status: ProcessingStatus }> {
  const response = await postJson<GenerateApiResponse>(resumePath(jobId));
  return {
    jobId: response.job_id ?? jobId,
    status: normalizeStatus(response.status),
  };
}

export async function confirmVideo(
  jobId: string,
): Promise<{ jobId: string; status: ProcessingStatus; waitingFor?: ChapterPlace }> {
  const response = await postJson<GenerateApiResponse>(`/api/v1/videos/${jobId}/confirm`);
  return {
    jobId: response.job_id ?? jobId,
    status: normalizeStatus(response.status),
    // A chapter approved while an earlier one renders waits for it.
    waitingFor: mapChapterPlace(response.waiting_for),
  };
}

export async function cancelVideo(jobId: string): Promise<{ jobId: string; status: ProcessingStatus }> {
  const response = await postJson<GenerateApiResponse>(cancelPath(jobId));
  const status = normalizeStatus(response.status);
  return {
    jobId: response.job_id ?? jobId,
    status: isTerminalStatus(status) ? status : 'cancelled',
  };
}

/*
 * Permanent deletion, from #67 — part of the job lifecycle, so it lives here
 * rather than in `videoGenerationApi`. Git's rename detection paired staging's
 * pre-split `videoApi.ts` with the generation half during the merge, which is
 * how it nearly landed in the wrong slice.
 *
 * A 404 is success. The endpoint is idempotent by intent and the UI's job is to
 * end up with the video gone: a second delete, a retry after a timeout that
 * actually landed, or a row another session already removed all arrive here as
 * 404, and treating that as failure would leave the user staring at an error
 * about a video that is not there.
 */
export async function deleteVideo(jobId: string): Promise<{ jobId: string }> {
  try {
    const response = await deleteJson<DeleteApiResponse | undefined>(statusPath(jobId));
    return { jobId: response?.video_id ?? response?.job_id ?? jobId };
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return { jobId };
    throw err;
  }
}
